import React, { useCallback, useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { fg } from '@atlaskit/platform-feature-flags';
import { Popup, type TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { useHubContext } from '@atlassian/company-hub-common';
import { KeyboardHighlightProvider } from '@atlassian/search-dialog/search-keyboard';

import { SearchTextField } from '../article-search-component/search-textfield';

import { HelpObjectSearchDropdown } from './search-dropdown';

export type HelpObjectSearchComponentProps = { isEditor: boolean; placeholderText?: string };

export const HelpObjectSearchComponent = ({
	isEditor,
	placeholderText,
}: HelpObjectSearchComponentProps): React.JSX.Element => {
	const [queryWord, setQueryWord] = useState<string>('');
	const [showDropdown, setShowDropdown] = useState(false);
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [_, setInputWidth] = useState(inputBoxRef.current?.offsetWidth);

	const {
		cloudId,
		env: { helpCenterAri, relativeBaseUrl },
	} = useHubContext();

	if (!helpCenterAri || !cloudId) {
		throw new Error('Help center Ari and cloud ID are required');
	}

	const shouldUseFixedSearchLink = fg('enable_fix_help_center_search_link');

	useEffect(() => {
		const listener = () => setInputWidth(inputBoxRef.current?.offsetWidth);
		const unbind = bind(window, {
			type: 'resize',
			listener,
		});
		return unbind;
	}, []);

	const closeDropdown = () => {
		if (showDropdown) {
			setShowDropdown(false);
		}
	};

	const trigger = useCallback(
		(triggerProps: TriggerProps) => {
			const handleInput = (searchInput: string) => {
				setQueryWord(searchInput);
				setShowDropdown(searchInput.trim().length > 0);
			};

			return (
				<Box ref={inputBoxRef}>
					<SearchTextField
						keyboardRef={inputRef}
						{...triggerProps}
						ref={triggerProps.ref}
						onInput={handleInput}
						isReadOnly={isEditor}
						placeholderText={placeholderText}
						showDropdown={showDropdown}
						queryWord={queryWord}
						searchBasePath={shouldUseFixedSearchLink ? relativeBaseUrl || undefined : undefined}
					/>
				</Box>
			);
		},
		[isEditor, placeholderText, queryWord, relativeBaseUrl, shouldUseFixedSearchLink, showDropdown],
	);

	return (
		<Popup
			shouldReturnFocus={false}
			content={(contentProps) => {
				return (
					<KeyboardHighlightProvider listenerNode={inputRef?.current}>
						<HelpObjectSearchDropdown
							{...contentProps}
							cloudId={cloudId}
							helpCenterAri={helpCenterAri || ''}
							inputWidth={inputBoxRef.current?.offsetWidth || 0}
							queryWord={queryWord}
						/>
					</KeyboardHighlightProvider>
				);
			}}
			isOpen={showDropdown}
			autoFocus={false}
			placement="bottom-start"
			trigger={trigger}
			onClose={closeDropdown}
		/>
	);
};
